(() => {
  'use strict';
  if (customElements.get('edvi-cart-progress')) return;
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  const recommendationCache = new Map();
  const hostState = new WeakMap();
  let adding = false;
  let activeAddition = null;
  let checkoutStarted = false;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const colors = value => (Array.isArray(value) ? value : value ? [value] : []).map(color => String(color).trim().toLowerCase());
  const tags = product => (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(tag => tag.trim().toLowerCase());
  const excluded = product => {
    const values = tags(product);
    return product.gift_card || /^opening-set-/.test(product.handle || '') || ['hidden', 'gift', 'bundle', 'edvi-category:hats', 'hats'].some(tag => values.includes(tag)) || /^(gift cards?|caps?|hats?|bundle)$/i.test(product.type || product.product_type || '');
  };
  const localURL = path => {
    const url = new URL(path.startsWith('/') ? path : root + path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid cart destination');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const getState = host => {
    if (!hostState.has(host)) hostState.set(host, { count: 0, scroll: 0, selections: new Map() });
    return hostState.get(host);
  };
  async function fetchResponse(url, accept) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: accept }, signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('Recommendation request failed');
    return response;
  }
  async function recommendations(id, config) {
    const key = [id, config.currency, config.locale].join(':');
    if (recommendationCache.has(key)) return recommendationCache.get(key);
    const pending = (async () => {
      const jsonURL = localURL('recommendations/products.json');
      jsonURL.searchParams.set('product_id', id);
      jsonURL.searchParams.set('limit', '8');
      jsonURL.searchParams.set('intent', 'related');
      const htmlURL = localURL('recommendations/products');
      htmlURL.search = jsonURL.search;
      htmlURL.searchParams.set('section_id', 'edvi-cart-contents');
      const [jsonResponse, htmlResponse] = await Promise.all([
        fetchResponse(jsonURL, 'application/json'),
        fetchResponse(htmlURL, 'text/html')
      ]);
      const [json, html] = await Promise.all([jsonResponse.json(), htmlResponse.text()]);
      const document = new DOMParser().parseFromString(html, 'text/html');
      const metadata = JSON.parse(document.querySelector('[data-edvi-upsell-data]')?.textContent || 'null');
      if (metadata?.source !== 'related' || metadata.currency !== config.currency) return [];
      const available = new Map((json.products || []).filter(product => product.available && !excluded(product)).map(product => [String(product.id), product]));
      return (metadata.products || []).filter(product => available.has(String(product.id)) && available.get(String(product.id)).variants?.some(variant => variant.available && String(variant.id) === String(product.variant_id)));
    })();
    recommendationCache.set(key, pending);
    try { return await pending; } catch (error) { recommendationCache.delete(key); throw error; }
  }
  async function gather(seeds, config) {
    const results = new Array(seeds.length);
    let next = 0;
    const worker = async () => {
      while (next < seeds.length) {
        const index = next++;
        try { results[index] = await recommendations(String(seeds[index].id), config); } catch { results[index] = []; }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, seeds.length) }, worker));
    return results.flat();
  }
  class CartProgress extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;
      const host = this.closest('[data-edvi-bag]');
      if (!host) return;
      const state = getState(host);
      const count = Math.max(0, Number(this.dataset.count) || 0);
      const before = Math.min(2, state.count) / 2 * 100;
      state.count = count;
      this.style.setProperty('--edvi-progress', before + '%');
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const body = this.closest('.edvi-bag-body');
        if (body && body.firstElementChild !== this) body.prepend(this);
        this.getBoundingClientRect();
        requestAnimationFrame(() => { if (this.isConnected) this.style.setProperty('--edvi-progress', Math.min(2, count) / 2 * 100 + '%'); });
      });
    }
  }
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;
      this.controller = new AbortController();
      try { this.config = JSON.parse(this.querySelector('[data-edvi-upsell-data]').textContent); } catch { this.hidden = true; return; }
      this.host = this.closest('[data-edvi-bag]');
      this.rail = this.querySelector('[data-upsell-rail]');
      if (!this.host || !this.rail) return;
      this.addEventListener('click', event => this.add(event), { signal: this.controller.signal });
      this.addEventListener('change', event => this.selectVariant(event), { signal: this.controller.signal });
      this.rail.addEventListener('scroll', () => { getState(this.host).scroll = this.rail.scrollLeft; }, { passive: true, signal: this.controller.signal });
      const dialog = this.closest('[data-cart-drawer]');
      if (!dialog || dialog.open) void this.load();
      else document.addEventListener('edvi-bag:open', () => void this.load(), { once: true, signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
    async load() {
      if (this.loading) return;
      this.loading = true;
      const config = this.config;
      const cartIDs = new Set(config.items.map(item => String(item.id)));
      const seeds = [...new Map(config.items.filter(item => !excluded(item)).map(item => [String(item.id), item])).values()];
      const frequency = new Map();
      config.items.filter(item => !excluded(item)).forEach(item => colors(item.colors).forEach(color => frequency.set(color, (frequency.get(color) || 0) + item.quantity)));
      const dominant = [...frequency].sort((a, b) => b[1] - a[1])[0]?.[0];
      const signature = JSON.stringify([config.currency, config.locale, [...cartIDs].sort(), dominant]);
      const state = getState(this.host);
      if (state.signature === signature && state.products) { this.paint(state.products); return; }
      if (activeAddition?.slots.has(this.host)) this.paint(state.products || []);
      const related = await gather(seeds, config);
      if (!this.isConnected) return;
      const unique = [...new Map(related.filter(product => !cartIDs.has(String(product.id))).map(product => [String(product.id), product])).values()];
      const fallback = config.products.filter(product => !cartIDs.has(String(product.id)) && !unique.some(item => String(item.id) === String(product.id)));
      const sameColorFirst = (a, b) => Number(colors(b.colors).includes(dominant)) - Number(colors(a.colors).includes(dominant));
      unique.sort(sameColorFirst);
      fallback.sort(sameColorFirst);
      const selected = [...unique, ...fallback].slice(0, 4);
      state.signature = signature;
      state.products = selected;
      this.paint(selected);
    }
    variant(product) {
      const selected = getState(this.host).selections.get(String(product.id));
      return product.variants?.find(variant => variant.available && String(variant.id) === selected)
        || product.variants?.find(variant => variant.available);
    }
    statusLabel(status) {
      return status === 'adding' ? 'Adding…'
        : status === 'added' ? 'Added ✓'
        : status === 'error' ? 'Try again'
        : this.config.labels.add;
    }
    paint(products) {
      if (!this.isConnected) return;
      this.baseProducts = products;
      const cartIDs = new Set(this.config.items.map(item => String(item.id)));
      const visible = products.filter(product => !cartIDs.has(String(product.id)) && product.variants?.some(variant => variant.available));
      const attempt = activeAddition;
      if (attempt?.slots.has(this.host) && attempt.status !== 'error') {
        const index = visible.findIndex(product => String(product.id) === String(attempt.product.id));
        if (index !== -1) visible.splice(index, 1);
        visible.splice(Math.min(attempt.slots.get(this.host), visible.length), 0, attempt.product);
      }
      this.products = visible.slice(0, 4);
      this.hidden = this.products.length === 0;
      const labels = this.config.labels;
      this.rail.innerHTML = this.products.map(product => {
        const id = escape(product.id);
        const url = escape(localURL(product.url));
        const title = escape(product.title);
        const image = product.image ? '<img class="edvi-cart-upsell__image" src="' + escape(product.image) + '" alt="" width="64" height="64" loading="lazy">' : '<span class="edvi-cart-upsell__image" aria-hidden="true"></span>';
        const selected = this.variant(product);
        const status = String(attempt?.product.id) === String(product.id) ? attempt.status : '';
        const locked = adding || checkoutStarted || status === 'added';
        const label = this.statusLabel(status);
        const accessible = status ? label + ': ' + product.title : labels.add_product.replace('__TITLE__', product.title);
        const action = '<button type="button" class="edvi-cart-upsell__add" data-upsell-add="' + id + '" aria-label="' + escape(accessible) + '"' + (status === 'adding' ? ' aria-busy="true"' : '') + (locked || !selected ? ' disabled' : '') + '>' + escape(label) + '</button>';
        const selector = product.variants.length > 1 ? '<select class="edvi-cart-upsell__variant" data-upsell-variant="' + id + '" aria-label="' + escape(labels.choose_product.replace('__TITLE__', product.title)) + '"' + (locked ? ' disabled' : '') + '>' + product.variants.map(variant => '<option value="' + escape(variant.id) + '"' + (variant.id === selected?.id ? ' selected' : '') + (!variant.available ? ' disabled' : '') + '>' + escape(variant.title) + '</option>').join('') + '</select>' : '';
        return '<article class="edvi-cart-upsell__card" data-upsell-product="' + id + '" role="listitem"><a href="' + url + '" aria-label="' + title + '">' + image + '</a><div class="edvi-cart-upsell__info"><a class="edvi-cart-upsell__title" href="' + url + '" title="' + title + '">' + title + '</a><span class="edvi-cart-upsell__price">' + escape(selected?.price || product.price) + '</span>' + action + '</div>' + selector + '</article>';
      }).join('');
      this.rail.setAttribute('aria-busy', 'false');
      this.rail.scrollLeft = getState(this.host).scroll;
      if (attempt?.slots.has(this.host)) this.feedback(attempt.status === 'error' ? labels.error : attempt.status === 'added' ? labels.added.replace('__TITLE__', attempt.product.title) : '', attempt.status === 'error');
    }
    selectVariant(event) {
      const select = event.target.closest('[data-upsell-variant]');
      if (!select) return;
      event.stopPropagation();
      const product = this.products?.find(item => String(item.id) === select.dataset.upsellVariant);
      const variant = product?.variants.find(item => item.available && String(item.id) === select.value);
      if (!variant || select.disabled) return;
      getState(this.host).selections.set(String(product.id), String(variant.id));
      select.closest('[data-upsell-product]').querySelector('.edvi-cart-upsell__price').textContent = variant.price;
    }
    async add(event) {
      if (event.target.closest('[data-upsell-variant]')) { event.stopPropagation(); return; }
      const button = event.target.closest('[data-upsell-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled || adding || checkoutStarted) return;
      const product = this.products?.find(item => String(item.id) === button.dataset.upsellAdd);
      const select = button.closest('[data-upsell-product]')?.querySelector('[data-upsell-variant]');
      const variant = select ? product?.variants.find(item => item.available && String(item.id) === select.value) : product && this.variant(product);
      if (!product || !variant) return;
      const host = this.host;
      const restoreFocus = document.activeElement === button;
      const slots = new Map();
      document.querySelectorAll('edvi-cart-upsell').forEach(element => {
        const index = element.products?.findIndex(item => String(item.id) === String(product.id));
        if (index >= 0) {
          slots.set(element.host, index);
          getState(element.host).selections.set(String(product.id), String(variant.id));
        }
      });
      const attempt = activeAddition = { product, status: 'adding', slots };
      const repaint = () => document.querySelectorAll('edvi-cart-upsell').forEach(element => {
        if (element.baseProducts) element.paint(element.baseProducts);
      });
      const focus = failed => {
        if (!restoreFocus || (document.activeElement !== document.body && document.activeElement !== button)) return;
        const target = host.querySelector(failed ? '[data-upsell-add="' + product.id + '"]' : '.edvi-cart-upsell__heading');
        target?.focus({ preventScroll: true });
      };
      adding = true;
      repaint();
      let succeeded = false;
      try {
        if (!window.EdviBag?.add) throw new Error('Cart unavailable');
        await window.EdviBag.add({ items: [{ id: variant.id, quantity: 1 }] });
        succeeded = true;
      } catch {
        // An add may succeed while the subsequent section refresh fails. Never add it twice.
        try {
          const response = await fetch(localURL('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(12000) });
          if (response.ok) succeeded = (await response.json()).items?.some(item => String(item.variant_id || item.id) === String(variant.id)) || false;
          if (succeeded) await window.EdviBag?.refresh();
        } catch {}
      }
      attempt.status = succeeded ? 'added' : 'error';
      if (!succeeded) adding = false;
      repaint();
      focus(!succeeded);
      if (succeeded) setTimeout(() => {
        if (activeAddition !== attempt) return;
        activeAddition = null;
        adding = false;
        document.querySelectorAll('edvi-cart-upsell').forEach(element => {
          if (!element.baseProducts) return;
          const remaining = element.baseProducts.filter(item => String(item.id) !== String(product.id));
          getState(element.host).products = remaining;
          element.paint(remaining);
        });
      }, 1500);
    }
    feedback(message, error = false) {
      const node = this.querySelector('.edvi-cart-upsell__feedback');
      if (!node) return;
      node.textContent = message;
      node.classList.toggle('is-error', error);
    }
  }
  document.addEventListener('edvi-bag:checkout-handoff', () => { checkoutStarted = true; });
  addEventListener('pageshow', () => { checkoutStarted = false; });
  customElements.define('edvi-cart-progress', CartProgress);
  customElements.define('edvi-cart-upsell', CartUpsell);
})();
